<template>
  <div
    class="pickup-list-item"
    @click="$emit('select')"
  >
    <div class="pickup-list-item__head">
      <template v-if="store.type === 'store'">
        <div class="pickup-list-item__head-store-title">
          Магазин
          <f-sprite-icon :icon-link="$options.icons.IconLogo.id" />
        </div>
      </template>
      <template v-else>
        <div class="pickup-list-item__head-alt-title">
          <template v-if="store.type === 'postamat'">
            <f-sprite-icon :icon-link="$options.icons.IconTerminal.id" />
          </template>
          <template v-else>
            <f-sprite-icon :icon-link="$options.icons.IconPickup.id" />
          </template>
          <span>{{ store.name }}</span>
        </div>
      </template>

      <products-count-tag
        :count="store.availableLinesCount"
        :total="ids.length"
        class="pickup-list-item__products-count"
      />
    </div>

    <div
      v-if="store.metro.length"
      class="pickup-list-item__metro"
    >
      <div
        v-for="metro in store.metro"
        :key="metro.id"
      >
        <span :style="{ backgroundColor: metro.line.color }" />{{ metro.name }}
      </div>
    </div>

    <div class="pickup-list-item__title">
      <span class="pickup-list-item__address">{{ store.address }}</span>
      <span v-if="distance" class="pickup-list-item__distance">{{ distance }}</span>
    </div>

    <div class="pickup-list-item__info">
      <div
        v-if="checkLabels(store)"
        class="pickup-list-item__info-labels"
      >
        <tag
          v-if="store.needPrepay"
          title="100%&nbsp;&nbsp;предоплата"
          width="120px"
          color="#ffb71b"
        />
        <tag
          v-if="checkQrCodeRequirement(store)"
          title="Вход по QR"
          width="80px"
          color="#C1E0E0"
        />
      </div>

      <div class="pickup-list-item__info-schedule">
        Часы работы: {{ store | parseWorkSchedule }}
      </div>
    </div>

    <div class="pickup-list-item__bottom">
      <div class="pickup-list-item__bottom-icons">
        <div
          v-if="hasFitting"
          class="pickup-list-item__bottom-icon"
        >
          <f-sprite-icon :icon-link="$options.icons.IconFitting.id" />
        </div>
        <div
          v-if="hasRefund"
          class="pickup-list-item__bottom-icon"
        >
          <f-sprite-icon :icon-link="$options.icons.IconReturn.id" />
        </div>
        <div
          v-if="hasCardPay"
          class="pickup-list-item__bottom-icon"
        >
          <f-sprite-icon :icon-link="$options.icons.IconCard.id" />
        </div>
      </div>

      <div
        v-if="store.availableOn"
        class="pickup-list-item__bottom-access"
      >
        <span>Можно забрать:</span> {{ store.availableOn | date({showYear: true}) }}
      </div>
    </div>
  </div>
</template>

<script>
import ProductsCountTag from '@/components/checkout/delivery-selector/products-count-tag.vue';
import Tag from "@kit/tag/tag";

import IconLogo from '@images/logo.multi.svg';
import IconFitting from '@icons/fitting.svg';
import IconCard from '@icons/cardpay.svg';
import IconPickup from '@icons/pvz.svg';
import IconTerminal from '@icons/postamat.svg';
import IconReturn from '@icons/return.svg';

export default {
  name: 'pickup-list-item',
  components: {
    ProductsCountTag,
    Tag,
  },
  icons: {
    IconLogo,
    IconFitting,
    IconCard,
    IconPickup,
    IconTerminal,
    IconReturn
  },

  props: {
    store: {
      type: Object,
      required: true
    },

    ids: {
      type: Array,
      default: () => []
    },

    distance: {
      type: String,
      default: '',
    }
  },

  computed: {
    date() {
      return new Date(this.store.availableOn);
    },

    day() {
      return this.date.getDay();
    },

    month() {
      return this.date.getMonth();
    },

    typeIsStore() {
      return this.store.type === 'store';
    },

    hasFitting() {
      return this.typeIsStore || this.store?.services?.includes('has_fitting');
    },

    hasRefund() {
      return this.typeIsStore || this.store?.services?.includes('has_refund');
    },

    hasCardPay() {
      return this.typeIsStore
          || this.store?.services?.includes('cash_and_card')
          || this.store?.services?.includes('card_only');
    },
  },

  methods: {
    checkLabels(store = {}) {
      return store.needPrepay || this.checkQrCodeRequirement(store);
    },

    checkQrCodeRequirement(store) {
      const { workMode } = store;
      return !!(workMode && workMode.toLowerCase() !== 'магазин');
    },
  }
};
</script>

<style lang="scss">
.pickup-list-item {
  padding: 18px 10px 16px;
  border-bottom: 1px solid $gray_lu;
  overflow: hidden;

  &__products-count {
    margin-left: 8px;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    &-store-title,
    &-alt-title {
      font-size: 14px;
      line-height: 24px;
    }

    &-store-title {
      display: flex;
      width: 145px;

      & > svg {
        height: 24px;
        margin-left: 6px;
      }
    }

    &-alt-title {
      display: inline;

      & > svg {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        margin-right: 8px;
        float: left;
        color: $gray;
      }
    }
  }

  &__metro {
    display: flex;
    flex-wrap: wrap;
    margin-top: 5px;
    font-size: 12px;
    line-height: 20px;
  }

  &__metro > div {
    display: flex;
    align-items: center;
    margin-right: 12px;

    & > span {
      display: block;
      width: 8px;
      height: 8px;
      margin-right: 8px;
      border-radius: 50%;
    }
  }

  &__title {
    display: flex;
    margin-top: 8px;
  }

  &__distance {
    flex-shrink: 0;
    margin-left: 10px;
    text-align: right;
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
    color: $gray;
    white-space: nowrap;
    opacity: .5;
  }

  &__address {
    display: block;
    flex-grow: 1;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
  }

  &__info {
    &-labels {
      .tag {
        display: inline-block;
        margin-left: 4px;
      }

      .tag:first-child {
        margin-left: 0;
      }
    }

    &-labels,
    &-schedule {
      margin-top: 8px;
    }

    &-schedule {
      font-size: 12px;
      line-height: 20px;
    }
  }

  &__bottom {
    margin-top: 8px;

    &-icons {
      display: flex;
      min-width: 74px;
      margin-left: auto;
      margin-top: 8px;
    }

    &-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 24px;
      height: 24px;
      margin-left: 4px;
      color: $gray;
      background-color: $gray_bgr;
      border-radius: 50%;

      svg {
        width: 14px;
        height: 14px;
      }
    }

    &-icon:first-child {
      margin-left: 0;
    }

    &-access {
      margin-top: 8px;
      font-size: 12px;
      line-height: 20px;

      & > span {
        margin-right: 8px;
        color: $blue;
      }
    }
  }
}

@include breakpoint("tablet") {
  .pickup-list-item {
    padding: 12px;

    &__head {
      &-alt-title {
        display: flex;
        align-items: center;
      }
    }

    &__metro {
      margin-top: 4px;
      line-height: 20px;
    }

    &__title {
      margin-top: 4px;
    }

    &__info {
      width: 100%;
      margin-right: 18px;
      display: flex;
      flex-direction: column-reverse;

      &-schedule {
        margin-top: 4px;
        line-height: 24px;
      }

      &-labels {
        margin-top: 4px;
        margin-bottom: 0;
      }
    }

    &__bottom {
      display: flex;
      flex-direction: row-reverse;
      align-items: flex-end;
      justify-content: space-between;
      margin-top: 4px;
      max-height: 20px;

      &-icons {
        display: flex;
        min-width: 93px;
        margin-left: auto;
        margin-top: 0;
      }

      &-icon {
        width: 32px;
        height: 32px;
        margin-left: 12px;

        svg {
          width: 19px;
          height: 19px;
        }
      }

      &-icon:last-child,
      &-icon:first-child {
        margin-top: 0;
      }

      &-icon:first-child {
        margin-left: auto;
      }

      &-access {
        margin-top: 0;
      }
    }
  }
}

@include breakpoint("desktop") {
  .pickup-list-item {
    transition: .2s background-color ease;
    cursor: pointer;

    &:hover {
      background-color: $gray_bgr;

      .pickup-list-item__bottom-icon {
        background-color: #fff;
      }
    }

    &__head {
      &-store-title,
      &-alt-title {
        font-size: 14px;
        line-height: 24px;
      }
    }

    &__address {
      flex-grow: 0;
    }

    &__distance {
      flex-grow: 1;
      flex-basis: 163px;
      margin-left: 4px;
    }

    &__metro {
      padding-left: 3px;
    }

    &__bottom {
      &-icon {
        transition: .2s background-color ease;
      }
    }
  }
}
</style>
